import React, { useEffect, useMemo, useRef, useState } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Work from './components/Work';
import Contact from './components/Contact';
import CaseStudy from './components/CaseStudy';
import SystemStudy from './components/SystemStudy';
import Icon from './components/Icon';
import { profile, projects, ownedProjects } from './data/portfolio';
import { parseRoute, routePath } from './utils/navigation';
import { getSEO, updateSEO } from './utils/seo';
import { trackPageView } from './utils/analytics';

function useRoute(initialUrl) {
  const readLocation = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const [hash, setHash] = useState(() => typeof window === 'undefined' ? initialUrl : readLocation());
  useEffect(() => {
    const update = () => {
      if (window.location.hash.startsWith('#/')) window.history.replaceState(null, '', routePath(parseRoute(window.location.hash)));
      setHash(`${window.location.pathname}${window.location.search}${window.location.hash}`);
    };
    const navigate = event => {
      const link = event.target.closest?.('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
      const url = new URL(link.href);
      if (url.origin !== window.location.origin || !link.getAttribute('href').startsWith('/') || !['home', 'work', 'study'].includes(parseRoute(url.pathname).type)) return;
      event.preventDefault();
      window.history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`);
      update();
    };
    window.addEventListener('hashchange', update);
    window.addEventListener('popstate', update);
    document.addEventListener('click', navigate);
    return () => { window.removeEventListener('hashchange', update); window.removeEventListener('popstate', update); document.removeEventListener('click', navigate); };
  }, []);
  const route = useMemo(() => parseRoute(hash), [hash]);
  return [route, hash];
}

export default function App({ initialUrl = '/' }) {
  const [route, hash] = useRoute(initialUrl);
  const firstRender = useRef(true);
  const project = projects.find(item => item.id === route.id);
  const featuredProject = ownedProjects.find(item => item.id === 'renderer');
  const missing = route.type === 'missing' || (route.type === 'study' && !project);
  const seo = getSEO(route, project);
  const title = seo.title;
  useEffect(() => {
    const seo = getSEO(route, project);
    updateSEO(seo);
    trackPageView({ route, seo });
    const target = route.section ? document.getElementById(route.section) : document.querySelector('main h1, main h2');
    if (route.section) {
      target?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      target?.querySelector('h2')?.focus({ preventScroll: true });
    } else if (!firstRender.current) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      target?.focus({ preventScroll: true });
    }
    firstRender.current = false;
  }, [hash, title, project, route]);
  return (
    <>
      <a className="skip-link" href="#main" onClick={event => { event.preventDefault(); document.getElementById('main')?.focus(); document.getElementById('main')?.scrollIntoView(); }}>Skip to content</a>
      <Navbar route={route} />
      <main id="main" className="page-shell" tabIndex={-1}>
        {missing ? (
          <section className="missing-page"><span className="eyebrow">A missing page</span><h1 tabIndex={-1}>This study isn’t here.</h1><p>Explore the engineering studies or return to the beginning.</p><a className="button" href="/work/">Browse the work <Icon /></a></section>
        ) : route.type === 'study' ? (
          <CaseStudy key={project.id} project={project} />
        ) : route.type === 'work' ? (
          <><header className="index-intro"><span className="eyebrow">The complete portfolio / Ownership in focus</span><h1 tabIndex={-1}>My work.<br /><em>The full picture.</em></h1><p>From a faster video renderer to backend platforms and QA architecture. Explore my owned projects, followed by credited collaborations.</p></header><Work full /></>
        ) : (
          <>
            <Hero />
            <Work />
            <section className="under-hood section" aria-labelledby="under-hood-title">
              <div className="section-heading"><div><span className="eyebrow">02 / Inside my Renderer rebuild</span><h2 id="under-hood-title">Better performance<br /><em>starts with a decision.</em></h2></div><p>Follow my production-deployed Renderer work from a slow frame-encoding loop to native recording and correctness checks. The 65-to-28-second result is from matched staging runs.</p></div>
              <SystemStudy project={featuredProject} compact />
              <div className="under-hood-link"><a className="text-link" href="/work/renderer/">Read my complete Renderer study <Icon /></a></div>
            </section>
            <About />
            <Contact />
          </>
        )}
      </main>
      <footer className="site-footer page-shell"><a className="footer-mark" href="/" onClick={() => window.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })}>VL<span>Vaibhav Lehra</span></a><p>Python. Platforms. Production.<span className="footer-note">Anonymous, cookie-free view counts.</span></p><a className="text-link" href={profile.github} target="_blank" rel="noopener noreferrer">GitHub <Icon kind="diagonal" /></a></footer>
    </>
  );
}
